/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useState } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	FormHelperText,
	TextField,
	FormLabel,
	Typography,
	Stack,
} from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useFormikHelper } from '@/hooks/useFormikHelper';
import validationSchema, { FOLDER_NAME, ERROR_MESSAGE, FOLDER_NAME_MAX } from './validationSchema';
import { useObjectContext } from '@/hooks/useObjectContext';
import { clientsApi } from '@/features/clients/clientsApi';
import { dealsApi } from '@/features/deals/dealsApi';
import { engagementsApi } from '@/features/engagements/engagementsApi';
import { relationshipCompaniesApi } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import { useAttachmentsContext } from '@/context/attachmentsContext';
import {
	useAddFolderToSharepointMutation,
	useLazySearchFoldersAndFilesQuery,
} from '@/features/msGraph/msGraphApi';
import { tryDecodeFilePath } from '@/utils/constants';

export const AddFolderSharepointDialog = () => {
	const params = useParams();
	const { dealRow } = useObjectContext();

	const { allFiles, clientFolderDriveId, refreshAttachmentsList } = useAttachmentsContext();
	const [loading, setLoading] = useState(false);

	const existingFolders = useMemo(
		() => allFiles.filter((f) => f.isFolder).map((f) => f.name),
		[allFiles],
	);

	const dispatch = useDispatch();

	const open = useSelector((state) => state.dialog.open['createFolder']);

	const [createSharepointFolder] = useAddFolderToSharepointMutation();
	const [searchFolder] = useLazySearchFoldersAndFilesQuery();

	const isClient = Boolean(params.clientId);

	const { itemId, searchValue } = useSelector((state) => state.attachmentsTable);

	const handleClose = () => {
		formik.resetForm();
		dispatch(closeDialog('createFolder'));
	};

	const handleCreateFolder = async () => {
		if (!itemId) return null;
		return await createSharepointFolder({
			driveId: clientFolderDriveId,
			itemId,
			folderName: formik.values[FOLDER_NAME].trim(),
		}).unwrap();
	};

	const formik = useFormik({
		initialValues: {
			[FOLDER_NAME]: '',
			[ERROR_MESSAGE]: '',
		},
		validationSchema: validationSchema(existingFolders),
		validateOnBlur: true,
		onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
			try {
				const response = await handleCreateFolder();
				if (searchValue.length > 0 || !response?.cachePatched) {
					refreshAttachmentsList();
				}
				dispatch(showToast({ message: 'Folder created successfully', type: 'success' }));
				setTimeout(async () => {
					try {
						const relationshipCompanyId = params?.relationshipCompanyId;
						if (relationshipCompanyId) {
							dispatch(
								relationshipCompaniesApi.util.invalidateTags([
									{ type: 'RELATIONSHIP_COMPANY', id: relationshipCompanyId },
								]),
							);
						} else {
							const type = (
								isClient ? 'client' : (dealRow?.objectType ?? '')
							).toUpperCase();
							const objectId = params?.dealId ?? params?.clientId;
							dispatch(
								isClient
									? clientsApi.util.invalidateTags([
											{ type: `${type}S`, id: 'LIST' },
											{ type, id: objectId },
											{ type: 'CLIENT_ROW', id: objectId },
										])
									: dealsApi.util.invalidateTags([
											{ type: `${type}S`, id: 'LIST' },
											{ type, id: objectId },
											{ type: 'DEAL_ROW', id: objectId },
										]),
							);
							dispatch(
								engagementsApi.util.invalidateTags([
									{ type: 'ENGAGEMENTS', id: objectId },
								]),
							);
						}
					} catch (error) {
						console.error('Error invalidating API:', error);
					}
				}, 2000);
				resetForm();
				handleClose();
			} catch (error) {
				const message =
					error?.data?.message ??
					error?.response?.data?.error?.message ??
					error?.message ??
					'There was an error. Please try again.';
				setFieldValue(ERROR_MESSAGE, `Error creating folder. ${message}`);
				dispatch(showToast({ message, type: 'error' }));
			} finally {
				setSubmitting(false);
			}
		},
	});

	const handleSubmit = async () => {
		setLoading(true);
		await formik.validateForm().then(async (errors) => {
			if (Object.keys(errors).length === 0) {
				const folderName = formik.values[FOLDER_NAME].trim();
				const { folders } = await searchFolder({
					driveId: clientFolderDriveId,
					itemId,
					searchText: folderName,
					exact: true,
				}).unwrap();
				console.log('xSEARCH FOLDERS', folders);
				if (
					folders.some(
						(f) =>
							tryDecodeFilePath(f?.name, folderName) &&
							f?.parentReference?.id == itemId,
					)
				) {
					formik.setFieldError(FOLDER_NAME, 'A folder with this name already exists.');
				} else {
					formik.handleSubmit();
				}
			}
		});
		setLoading(false);
	};

	const { getError, getErrorMessage } = useFormikHelper(formik);
	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			sx={{ minWidth: '50%', padding: '1em' }}
		>
			<form onSubmit={formik.handleSubmit}>
				<DialogTitle>Create Folder</DialogTitle>
				<DialogContent sx={{ paddingBottom: '2em' }}>
					<FormLabel required>Folder Name</FormLabel>
					<TextField
						fullWidth
						name={FOLDER_NAME}
						{...formik.getFieldProps(FOLDER_NAME)}
						error={getError(FOLDER_NAME)}
						// helperText={getErrorMessage(FOLDER_NAME)}
						disabled={formik.isSubmitting}
						required
					/>
					<Stack direction='row' justifyContent='space-between'>
						<FormHelperText
							error={
								Boolean(formik.values[ERROR_MESSAGE]) ||
								(formik.touched[FOLDER_NAME] && Boolean(formik.errors[FOLDER_NAME]))
							}
						>
							{formik.values[ERROR_MESSAGE] || formik.errors[FOLDER_NAME]}
						</FormHelperText>

						<FormHelperText
							error={
								(formik.touched[FOLDER_NAME] &&
									Boolean(formik.errors[FOLDER_NAME])) ||
								formik.values[FOLDER_NAME].length > FOLDER_NAME_MAX
							}
							sx={{
								...(formik.values[FOLDER_NAME].length >= FOLDER_NAME_MAX && {
									fontWeight: 'bold',
								}),
								mr: '14px',
							}}
						>
							<span>{formik.values[FOLDER_NAME].length}</span>
							{`/${FOLDER_NAME_MAX}`}
						</FormHelperText>
					</Stack>
					{/* {formik.values[ERROR_MESSAGE] && (
						<FormHelperText error>{formik.values[ERROR_MESSAGE]}</FormHelperText>
					)} */}
					<Typography
						variant='caption'
						sx={{
							display: 'block',
							mt: '1.5em',
							color: 'text.secondary',
						}}
					>
						<span>
							{'Folders cannot contain the following characters:'}
							<span style={{ display: 'block' }}>{'< > : " \' /\\ | ? % *'}</span>
							{'or end in a .'}
						</span>
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color='primary'
						disabled={loading || formik.isSubmitting}
					>
						Cancel
					</Button>
					<Button
						// type='submit'
						variant='contained'
						color='primary'
						disabled={formik.isSubmitting || loading}
						onClick={async () => await handleSubmit()}
					>
						{formik.isSubmitting || loading ? (
							<CircularProgress size='2em' sx={{ color: '#FFFFFF' }} />
						) : (
							'Create'
						)}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
